import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { VendorService } from '@app/shared/services/vendor.service';
import { stars } from '@app/shared/constants/constants';
import {
  contractorQuarterlyFeedback,
  ErrorMessages,
  LoadingMessages,
  ButtonLabels,
  ContractorListEnum,
  PerformanceRating,
  PERFORMANCE_RATING_SHORT_LABEL,
  PERFORMANCE_RATING_OPTIONS,
  formatQuarterBoxLabel,
} from '@app/shared/constants/global.enum';
import {
  EmployeeDetailsResponse,
  ProfileItemConfig,
  mapEmployeeToProfileSections
} from './ops-contractor-profile.config';
import { OpsContractorsListView } from '../ops-contractors-list/ops-contractors-list.config';
import { TimeSheet } from '../ops-timesheet/ops-timesheet-config';

interface FeedbackDTO {
  createdBy?: string | null;
  createdDate?: string | null;
  updatedBy?: string | null;
  updatedDate?: string | null;
  contractorFeedbackId?: number | null;
  employeeId?: string | null;
  quarter?: string | null;
  year?: number | null;
  techincalRating?: number | null;
  behaviouralRating?: number | null;
  performanceRating?: string | null;
  comments?: string | null;
  submittedBy?: string | null;
  submittedDate?: string | null;
}

interface QuarterBox {
  quarter: string;
  year: number;
  label: string;
  isCurrent: boolean;
  feedbacks: FeedbackDTO[];
  loading: boolean;
  error?: string | null;
}

@Component({
  selector: 'app-ops-contractor-profile',
  templateUrl: './ops-contractor-profile.component.html',
  styleUrls: ['./ops-contractor-profile.component.scss'],
})
export class OpsContractorProfileComponent implements OnInit {
  contractorId: string | null = null;
  ContractorName: string = '';
  ContractorProfile: string = '';

  loading: boolean = false;
  error: string | null = null;
  hasData: boolean = false;

  aboutItems: ProfileItemConfig[] = [];
  teamInfoItems: ProfileItemConfig[] = [];
  performanceItems: ProfileItemConfig[] = [];
  skillItems: ProfileItemConfig[] = [];

  stars = stars;

  quarterlyFeedback = contractorQuarterlyFeedback;
  errorMessages = ErrorMessages;
  loadingMessages = LoadingMessages;
  buttonLabels = ButtonLabels;
  contractorListEnum = ContractorListEnum;

  quarterBoxes: QuarterBox[] = [];

  // DB-driven visible quarter set. null = fetch not yet completed.
  private visibleQuarterSet: Set<string> | null = null;

  @Input() contractorData: OpsContractorsListView | TimeSheet | null = null;
  @Output() close = new EventEmitter<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private vendorService: VendorService
  ) {}

  ngOnInit(): void {
    if (this.contractorData) {
      this.contractorId = this.contractorData.employeeId?.toString() || null;
    }
    this.error = this.contractorId ? null : this.errorMessages.NoEmployeeId;

    if (this.contractorId) {
      this.ensureVisibleQuartersLoaded(() => {
        this.buildQuarterBoxes();
        this.fetchQuarterlyFeedback();
      });
      this.loadEmployeeDetails();
    }
  }

  private ensureVisibleQuartersLoaded(cb: () => void): void {
    if (this.visibleQuarterSet !== null) { cb(); return; }
    this.vendorService.getVisibleFeedbackQuarters().subscribe({
      next: (list) => { this.visibleQuarterSet = new Set(list ?? []); cb(); },
      error: () => { this.visibleQuarterSet = new Set(); cb(); },
    });
  }

  private applyVisibleQuarterFilter(boxes: QuarterBox[]): QuarterBox[] {
    if (this.visibleQuarterSet === null) return boxes;
    const seen = new Set<string>();
    return boxes.filter((b) => {
      if (!this.visibleQuarterSet!.has(b.quarter)) return false;
      if (seen.has(b.quarter)) return false;
      seen.add(b.quarter);
      return true;
    });
  }

  private buildQuarterBoxes(): void {
    const now = new Date();
    const boxes: QuarterBox[] = [];

    for (let i = 0; i < 2; i++) {
      const d = new Date(now.getFullYear(), now.getMonth(), 1);
      d.setMonth(d.getMonth() - i * 3);
      const qNum = Math.floor(d.getMonth() / 3) + 1;
      boxes.push({
        quarter: `Q${qNum}`,
        year: d.getFullYear(),
        label: formatQuarterBoxLabel(qNum, d.getFullYear()),
        isCurrent: i === 0,
        feedbacks: [],
        loading: true,
        error: null
      });
    }

    this.quarterBoxes = this.applyVisibleQuarterFilter(boxes);
  }

  private fetchQuarterlyFeedback(): void {
    if (!this.contractorId) return;
    this.quarterBoxes.forEach(box => {
      box.loading = true;
      box.error = null;
      this.vendorService
        .getQuarterlyFeedback(this.contractorId as string, String(box.year), box.quarter)
        .subscribe({
          next: (resp: any) => {
            const payload = resp?.data ?? resp;
            const arr: FeedbackDTO[] = Array.isArray(payload)
              ? payload
              : (payload ? [payload] : []);
            arr.sort((a, b) =>
              new Date(b.submittedDate || 0).getTime() -
              new Date(a.submittedDate || 0).getTime()
            );
            box.feedbacks = arr;
            box.loading = false;
          },
          error: () => {
            box.feedbacks = [];
            box.error = 'Failed to load';
            box.loading = false;
          }
        });
    });
  }

  trackFeedback(index: number, fb: FeedbackDTO) {
    return fb.contractorFeedbackId ?? index;
  }

  /** Safe display-label lookup for a raw performanceRating string. Falls back
   *  to the raw value on unknown / null so untyped rows still render something. */
  getPerformanceRatingLabel(raw?: string | null): string {
    if (!raw) return '';
    const key = raw as PerformanceRating;
    return PERFORMANCE_RATING_SHORT_LABEL[key] ?? raw;
  }

  /** Full "A — High performer" display for historical / detail surfaces. */
  getPerformanceRatingFullDisplay(raw?: string | null): string {
    if (!raw) return '';
    const opt = PERFORMANCE_RATING_OPTIONS.find(o => o.value === raw);
    return opt ? opt.label : raw;
  }

  private loadEmployeeDetails(): void {
    if (!this.contractorId) {
      this.error = this.errorMessages.NoEmployeeId;
      return;
    }
    this.loading = true;
    this.error = null;
    this.hasData = false;
    this.vendorService.getEmployeeDetails(this.contractorId).subscribe({
      next: (response) => {
        this.loading = false;
        if (response?.status === this.contractorListEnum.Success && response?.data) {
          this.mapEmployeeDataToItems(response.data);
          this.hasData = true;
        } else {
          this.error = response?.status === this.contractorListEnum.Failed
            ? response?.message || `${this.errorMessages.EmployeeNotFound} with ID ${this.contractorId}`
            : this.errorMessages.InvalidResponse;
        }
      },
      error: () => {
        this.loading = false;
        this.hasData = false;
        this.error = this.errorMessages.EmployeeNotFound;
      }
    });
  }

  private mapEmployeeDataToItems(data: EmployeeDetailsResponse): void {
    this.ContractorName = data.employeeName || this.errorMessages.UnknownEmployeeName;
    this.ContractorProfile = data.jobRole || this.errorMessages.UnknownRole;
    const profileSections = mapEmployeeToProfileSections(data);
    this.aboutItems = profileSections.about;
    this.teamInfoItems = profileSections.teamInfo;
    this.performanceItems = profileSections.performance;
    this.skillItems = profileSections.skills;
  }

  private formatDate(input?: string | null): string | null {
    return input ? input.slice(0, 10) : null; // "YYYY-MM-DD"
  }

  retryLoading(): void {
    this.loadEmployeeDetails();
  }


  goBack(): void {
    this.router.navigate(['/ops-vendor-management']);
  }

  navigateToVendorManagement(): void {
    this.router.navigate(['ops-vendor-management']);
  }

  navigateToContractorsList(): void {
    this.close.emit();
  }
}
